import { APIResponseError, type ApiResponseLike } from "@web-monorepo/infra/responseHandlers";
import type { EnrichedError } from "@web-monorepo/telemetry";
import { NoNetworkError } from "@web-monorepo/dojo-fetch";
import { SAFARI_LOAD_FAILED_MARKER } from "./safariSpecialCase";
import { sanitizeIds } from "./sanitizeIds";

export const makeApiErrorMessage = ({
  response,
  path,
  name,
  method,
}: {
  response: ApiResponseLike;
  path: string;
  method: string;
  name: string;
}) => {
  return `Network Error ${response.statusCode} ${method} ${path}${name ? ` [${name}]` : ""}`;
};

/**
 * Structural stand-in for superagent's `ResponseError`, kept so the handful of callers that only
 * read `status` / `response` keep compiling now that superagent is gone. New code should prefer
 * `APIResponseError` / `isApiResponseError`.
 */
export type ResponseError = Error & {
  status?: number;
  response?: ApiResponseLike;
  timeout?: boolean;
};

export const isApiError = (
  error: unknown,
): error is Error & {
  response: ApiResponseLike;
  request: { path: string; url: string; method: string; body: unknown };
} => error instanceof Error && "response" in error && "request" in error;

export interface ApiErrorDetails {
  type: string;
  message: string;
  detail: string;
  code: string;
  fallbackMessage: string;
  expected: boolean;
}

function isApiErrorDetails(value: unknown): value is ApiErrorDetails {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const error = value as Record<string, unknown>;
  return (
    typeof error.type === "string" &&
    typeof error.message === "string" &&
    typeof error.detail === "string" &&
    typeof error.code === "string" &&
    typeof error.fallbackMessage === "string" &&
    typeof error.expected === "boolean"
  );
}

export function createErrorFromResponse(
  response: ApiResponseLike,
  route?: string,
): FetchersAPIResponseError | NetworkError {
  const responseBody = response.body as { error?: ApiErrorDetails };
  if (isApiErrorDetails(responseBody?.error)) {
    return new FetchersAPIResponseError(response, route);
  }
  return new NetworkError(response, route);
}

export class FetchersAPIResponseError<ErrorDetailsType extends ApiErrorDetails = ApiErrorDetails>
  extends APIResponseError
  implements EnrichedError
{
  type: ErrorDetailsType["type"];
  message: ErrorDetailsType["message"];
  detail: ErrorDetailsType["detail"];
  code: ErrorDetailsType["code"];
  fallbackMessage: ErrorDetailsType["fallbackMessage"];
  expected: ErrorDetailsType["expected"];

  tags?: Record<string, string>;
  extras?: Record<string, unknown>;

  constructor(response: ApiResponseLike, route?: string) {
    super(response);

    this.name = "FetchersAPIResponseError";

    // The goal here is to allow React Query consumers to do things like:
    //
    // const { data, error } = useSession();
    // if (error && error.code === 'whatever') { ... }
    //
    // Instead of error.response.body.error.code (probably with all optional chainings...):

    const responseBody = response.body as { error?: ErrorDetailsType };
    if (isApiErrorDetails(responseBody?.error)) {
      this.type = responseBody.error.type;
      this.message = responseBody.error.message;
      this.detail = responseBody.error.detail;
      this.code = responseBody.error.code;
      this.fallbackMessage = responseBody.error.fallbackMessage;
      this.expected = responseBody.error.expected;
    } else {
      const e = new Error("Non-Standard Error Parsed for FetchersAPIResponseError");
      e.cause = response;
      throw e;
    }

    const sanitizedRoute = route ? sanitizeIds(route) : sanitizeIds(this.request.path ?? "");
    this.tags = { route: sanitizedRoute };

    if (this.requestId) {
      this.tags = { ...this.tags, "x-dojo-request-id": this.requestId };
    }
  }
}

export { NoNetworkError };

export class NetworkError extends APIResponseError implements EnrichedError {
  tags?: Record<string, string>;

  constructor(response: ApiResponseLike, route?: string) {
    super(response);
    this.name = "NetworkError";
    const sanitizedRoute = route ? sanitizeIds(route) : sanitizeIds(this.request.path ?? "");
    this.tags = { route: sanitizedRoute, status: String(response.status ?? response.statusCode) };
  }
}

export class EmptyResponseBodyError extends Error implements EnrichedError {
  contexts?: Record<string, Record<string, unknown>>;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];

  constructor({
    path,
    method,
    url,
    headers,
    status,
    route,
  }: {
    path: string;
    method: string;
    url: string;
    headers: Record<string, string>;
    status: number;
    route?: string;
  }) {
    const sanitizedRoute = route ? sanitizeIds(route) : sanitizeIds(path);
    super(`Received ${status} response with empty body`);
    this.name = "EmptyResponseBodyError-v6";

    const sanitizedPath = sanitizeIds(path);

    this.tags = {
      errorType: "empty_response_body",
      method,
      status: String(status),
      route: sanitizedRoute,
      isIntentional: headers["x-intentionally-empty"] === "true" ? "true" : "false",
    };

    this.extra = {
      path: sanitizedPath,
      status,
    };

    this.fingerprint = ["empty-response-body-v6", method];

    const allowlistedHeaderKeys = [
      "content-type",
      "content-length",
      "x-client-identifier",
      "x-client-version",
      "x-sign-attachment-urls",
      "via",
      "x-backend-group",
      "x-dojo-request-id",
    ];

    const safeHeaders: Record<string, string> = {};
    for (const key of allowlistedHeaderKeys) {
      if (headers[key]) {
        safeHeaders[key] = headers[key];
      }
    }

    this.contexts = {
      request: {
        url,
        method,
        path: sanitizedPath,
        ...urlParts(url),
      },
      response: {
        status,
        headers: safeHeaders,
      },
    };
  }
}

function urlParts(url: string) {
  try {
    const parsed = new URL(url, typeof document !== "undefined" ? document.location.href : "https://example.com");

    return {
      host: parsed.host,
      pathname: parsed.pathname,
      searchParams: [...parsed.searchParams.keys()],
    };
  } catch (e) {
    if (e instanceof TypeError) {
      return {
        host: "error getting host",
        pathname: "error getting pathname",
      };
    }
    throw e;
  }
}

export class InvalidCollectionResponseError extends Error implements EnrichedError {
  contexts?: Record<string, Record<string, unknown>>;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];

  constructor({
    path,
    method,
    url,
    headers,
    status,
    route,
  }: {
    path: string;
    method: string;
    url: string;
    headers: Record<string, string>;
    status: number;
    route?: string;
  }) {
    const sanitizedRoute = route ? sanitizeIds(route) : sanitizeIds(path);
    super(`Collection response missing _items field`);
    this.name = "InvalidCollectionResponseError";

    const sanitizedPath = sanitizeIds(path);

    this.tags = {
      errorType: "invalid_collection_response",
      method,
      status: String(status),
      route: sanitizedRoute,
    };

    this.extra = {
      path: sanitizedPath,
      status,
    };

    this.fingerprint = ["invalid-collection-response", method];

    const allowlistedHeaderKeys = [
      "content-type",
      "content-length",
      "x-client-identifier",
      "x-client-version",
      "x-sign-attachment-urls",
      "via",
      "x-backend-group",
      "x-dojo-request-id",
    ];

    const safeHeaders: Record<string, string> = {};
    for (const key of allowlistedHeaderKeys) {
      if (headers[key]) {
        safeHeaders[key] = headers[key];
      }
    }

    this.contexts = {
      request: {
        url,
        method,
        path: sanitizedPath,
        ...urlParts(url),
      },
      response: {
        status,
        headers: safeHeaders,
      },
    };
  }
}

export function isNoNetworkError(error: Error): boolean {
  return (
    error.name === "NoNetworkError" ||
    error.message.startsWith("Network error when fetching ") ||
    (typeof error.stack === "string" && error.stack.startsWith("NoNetworkError:"))
  );
}

export function isEmptyResponseBodyError(error: Error): boolean {
  return error.name === "EmptyResponseBodyError-v6" || error.name === "EmptyResponseBodyError";
}

// Browser fetch errors that bypass apiFetch wrapping — Chrome "Failed to fetch",
// Safari "Load failed" / "[Safari Load Failed]", Firefox "NetworkError when attempting
// to fetch resource".
export function isRawNetworkError(error: Error): boolean {
  // `NormalizedNetworkError` (safariSpecialCase.ts) re-throws Safari's "Load failed" TypeError
  // as its own class, so its `name` is NOT "TypeError" — it must be matched before the name
  // guard below. Leaving it after made this branch unreachable, and every Safari load failure
  // fell through `isConnectivityError` into the generic `app_broken` unknown banner instead of
  // the network fallback (WEBP-184).
  if (error.message.includes(SAFARI_LOAD_FAILED_MARKER)) return true;
  if (error.name !== "TypeError") return false;
  return (
    error.message === "Failed to fetch" ||
    error.message === "Load failed" ||
    error.message.includes("NetworkError when attempting to fetch resource")
  );
}

// The user's connectivity failed, not our code: the fetch died before any HTTP
// response arrived (NoNetworkError / raw browser TypeError / empty body), or an API
// error carried status 0 (no response received). App error handlers route these to a
// network fallback instead of a generic broken-app surface.
export function isConnectivityError(error: Error): boolean {
  if (isNoNetworkError(error) || isEmptyResponseBodyError(error) || isRawNetworkError(error)) return true;
  if (isApiError(error) && (error.response.status === 0 || error.response.statusCode === 0)) return true;
  return error.message.includes("Error returned in API call: 0");
}
